import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfigPinApi } from '@/api/config-pin';
import { errMsg } from '@/api/errors';
import { PinError, PinField } from './ConfigPinFields';

export type ConfigPinSetFormProps = {
  /** true : premier code (mode strict, gérant) — l'ancien code n'est pas demandé. */
  firstPin: boolean;
  onDone: () => void;
  onBack: () => void;
  /** Proposé seulement pour le premier code : entrer sans créer de code maintenant. */
  onSkip?: () => void;
};

/** Création (premier code) ou changement du code d'accès Configuration. */
export function ConfigPinSetForm({ firstPin, onDone, onBack, onSkip }: ConfigPinSetFormProps) {
  const [oldPin, setOldPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const invalid = (!firstPin && oldPin.length < 4) || newPin.length < 4 || newPin !== confirmPin;

  const submit = async (): Promise<void> => {
    if (invalid) return;
    setBusy(true);
    setError(null);
    const res = await ConfigPinApi.set(firstPin ? '' : oldPin, newPin);
    setBusy(false);
    if (!res.ok) {
      setError(errMsg(res));
      return;
    }
    toast.success(firstPin ? 'Code Configuration créé' : 'Code Configuration mis à jour');
    onDone();
  };

  const clear = (fn: (v: string) => void) => (v: string) => {
    fn(v);
    setError(null);
  };

  return (
    <div className="space-y-3 py-2">
      {firstPin ? (
        <p className="text-sm text-muted-foreground">
          Votre commerce n&apos;a pas encore de code. Créez-le : votre équipe en aura besoin pour
          ouvrir la Configuration.
        </p>
      ) : (
        <PinField label="Ancien code" value={oldPin} onChange={clear(setOldPin)} autoFocus />
      )}
      <PinField
        label="Nouveau code (4 chiffres minimum)"
        value={newPin}
        onChange={clear(setNewPin)}
        autoFocus={firstPin}
      />
      <PinField
        label="Confirmer le nouveau code"
        value={confirmPin}
        onChange={clear(setConfirmPin)}
      />
      <PinError error={error} />
      <div className="flex gap-2 pt-2">
        <Button variant="outline" onClick={onBack} className="flex-1" disabled={busy}>
          {firstPin ? 'Annuler' : 'Retour'}
        </Button>
        <Button
          onClick={() => void submit()}
          disabled={busy || invalid}
          className="navy-gradient flex-1 text-primary-foreground"
        >
          {busy ? 'Enregistrement…' : firstPin ? 'Créer le code' : 'Changer le code'}
        </Button>
      </div>
      {onSkip ? (
        <button
          type="button"
          onClick={onSkip}
          className="w-full text-center text-xs text-muted-foreground underline hover:text-foreground"
        >
          Entrer sans créer de code pour l&apos;instant
        </button>
      ) : null}
    </div>
  );
}
